// Kenneth-only feature adoption dashboard at /dev/usage-stats.
// Queries existing tables via SECURITY DEFINER RPC — no new tracking, no third-party analytics.
import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../supabase'

const OWNER_ID = '26680bcf-f67b-490e-ba08-e1f25ddcfd01'

const C = {
  bg: '#F2EDE4', card: '#FDFAF6', border: '#DDD4C6',
  amber: '#B8721F', amberLight: '#D4902A', brown: '#2C1D14',
  muted: '#9A7B68', success: '#2E6B4A', successBg: '#EBF5EF',
  danger: '#B03020', dangerBg: '#FDECEA',
}
const geo = 'Georgia, serif'
const cal = 'Calibri, sans-serif'

function pct(n, total) {
  if (!total || !n) return 0
  return Math.round((n / total) * 100)
}

function SectionLabel({ children }) {
  return (
    <div style={{
      fontSize: 11, color: C.muted, fontFamily: cal,
      letterSpacing: '1.5px', textTransform: 'uppercase',
      marginBottom: 10, marginTop: 4,
    }}>
      {children}
    </div>
  )
}

function StatCard({ label, value, sub }) {
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`,
      borderRadius: 10, padding: 18, textAlign: 'center',
    }}>
      <div style={{ fontSize: 38, fontWeight: 700, color: C.amber, fontFamily: geo, lineHeight: 1 }}>
        {value ?? '—'}
      </div>
      <div style={{ fontSize: 13, color: C.brown, fontFamily: cal, marginTop: 6 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: C.muted, fontFamily: cal, marginTop: 3 }}>{sub}</div>}
    </div>
  )
}

function MetricBar({ name, count, total }) {
  const p = pct(count, total)
  const barColor = p >= 50 ? C.success : p >= 20 ? C.amber : C.muted
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`,
      borderRadius: 10, padding: '12px 16px', marginBottom: 8,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <span style={{ fontSize: 13, color: C.brown, fontFamily: cal }}>{name}</span>
        <span style={{ fontSize: 12, color: C.muted, fontFamily: cal, flexShrink: 0, marginLeft: 12 }}>
          {count} · <strong style={{ color: C.brown }}>{p}%</strong>
        </span>
      </div>
      <div style={{ height: 5, background: '#E8E0D4', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{
          height: '100%', width: `${p}%`, background: barColor,
          borderRadius: 3, transition: 'width 0.7s ease',
        }} />
      </div>
    </div>
  )
}

function DistGrid({ title, dist }) {
  const rows = [
    { label: 'None (0)', key: 'zero' },
    { label: '1–3', key: 'one_to_three' },
    { label: '4–6', key: 'four_to_six' },
    { label: '7+', key: 'seven_plus' },
  ]
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.brown, fontFamily: geo, marginBottom: 12 }}>
        {title}
      </div>
      {rows.map(({ label, key }) => (
        <div key={key} style={{
          display: 'flex', justifyContent: 'space-between',
          fontSize: 13, fontFamily: cal, color: C.brown,
          padding: '5px 0', borderBottom: `1px solid ${C.border}`,
        }}>
          <span style={{ color: C.muted }}>{label}</span>
          <span style={{ fontWeight: 600 }}>{dist?.[key] ?? 0}</span>
        </div>
      ))}
    </div>
  )
}

export default function UsageStats({ session }) {
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)
  const [lastUpdated, setLastUpdated] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setErr(null)
    const { data, error: rpcErr } = await supabase.rpc('get_usage_stats')
    if (rpcErr) {
      setErr(rpcErr.message)
    } else {
      setStats(data)
      setLastUpdated(new Date())
    }
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  // Belt-and-suspenders guard (App.js already gates this route)
  if (!session?.user || session.user.id !== OWNER_ID) {
    window.location.replace('/')
    return null
  }

  const total = stats?.total_users ?? 0

  const features = stats ? [
    { name: 'Appliances added',         count: stats.has_appliance },
    { name: 'Home setup complete',       count: stats.homes_setup },
    { name: 'NDA accepted',             count: stats.nda_accepted },
    { name: 'Get Unstuck (bug report)',  count: stats.has_bug_report },
    { name: 'Emergency contact added',   count: stats.has_emergency },
    { name: 'Household member added',    count: stats.has_household },
    { name: 'Maintenance task logged',   count: stats.has_maint_log },
    { name: 'Document uploaded',         count: stats.has_document },
    { name: 'Paint color recorded',      count: stats.has_paint },
    { name: 'Notification pref set',     count: stats.has_notif_pref },
    { name: 'Non-English language',      count: stats.non_default_lang },
  ] : []

  const sorted = [...features].sort((a, b) => b.count - a.count)
  const topFeature = sorted[0]
  const bottomFeature = sorted[sorted.length - 1]

  return (
    <div style={{ minHeight: '100vh', background: C.bg, padding: '24px 16px 48px' }}>
      <div style={{ maxWidth: 680, margin: '0 auto' }}>

        {/* ── HEADER ── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
          <div>
            <div style={{ fontSize: 11, color: C.amber, fontFamily: cal, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: 4 }}>
              DEV · OWNER ONLY
            </div>
            <h1 style={{ margin: 0, fontSize: 26, fontFamily: geo, color: C.brown }}>Usage Stats</h1>
            <div style={{ fontSize: 12, color: C.muted, fontFamily: cal, marginTop: 4 }}>
              Queries existing tables only · no new tracking
            </div>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 16 }}>
            <button
              onClick={load}
              disabled={loading}
              style={{
                background: `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`,
                border: 'none', borderRadius: 8, padding: '9px 18px',
                fontSize: 13, fontFamily: geo, fontWeight: 700,
                color: '#fff', cursor: loading ? 'default' : 'pointer',
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? '...' : '↻ Refresh'}
            </button>
            {lastUpdated && (
              <div style={{ fontSize: 11, color: C.muted, fontFamily: cal, marginTop: 6 }}>
                Updated {lastUpdated.toLocaleTimeString()}
              </div>
            )}
          </div>
        </div>

        {/* ── ERROR ── */}
        {err && (
          <div style={{
            background: C.dangerBg, border: `1px solid ${C.danger}44`,
            borderRadius: 10, padding: '12px 16px',
            color: C.danger, fontFamily: cal, fontSize: 13, marginBottom: 20,
          }}>
            {err}
          </div>
        )}

        {/* ── LOADING PLACEHOLDER ── */}
        {loading && !stats && (
          <div style={{ textAlign: 'center', color: C.muted, fontFamily: cal, padding: 60, fontSize: 14 }}>
            Loading stats...
          </div>
        )}

        {stats && (
          <>
            {/* ── SECTION A — USER TOTALS ── */}
            <SectionLabel>A — User Totals</SectionLabel>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 28 }}>
              <StatCard label="Registered users" value={stats.total_users} sub="auth.users" />
              <StatCard label="Home setup complete" value={stats.homes_setup} sub={`${pct(stats.homes_setup, total)}% of total`} />
              <StatCard label="Active last 30 days" value={stats.active_30d} sub={`${pct(stats.active_30d, total)}%`} />
            </div>

            {/* ── SECTION D — AT A GLANCE ── */}
            <SectionLabel>D — At a Glance</SectionLabel>
            <div style={{
              background: C.card, border: `1px solid ${C.border}`,
              borderRadius: 10, padding: '14px 18px', marginBottom: 28,
            }}>
              <div style={{ fontSize: 13, fontFamily: cal, color: C.brown, marginBottom: 7 }}>
                <span style={{ color: C.success, fontWeight: 700 }}>Most adopted: </span>
                {topFeature?.name} ({pct(topFeature?.count, total)}%)
              </div>
              <div style={{ fontSize: 13, fontFamily: cal, color: C.brown }}>
                <span style={{ color: C.danger, fontWeight: 700 }}>Least adopted: </span>
                {bottomFeature?.name} ({pct(bottomFeature?.count, total)}%)
              </div>
            </div>

            {/* ── SECTION B — FEATURE ADOPTION ── */}
            <SectionLabel>B — Feature Adoption (% of {total} registered users)</SectionLabel>
            <div style={{ marginBottom: 28 }}>
              {features.map(feat => (
                <MetricBar key={feat.name} name={feat.name} count={feat.count} total={total} />
              ))}
            </div>

            {/* ── SECTION C — DISTRIBUTIONS ── */}
            <SectionLabel>C — Distributions</SectionLabel>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 32 }}>
              <DistGrid title="Appliances per user" dist={stats.appliances_dist} />
              <DistGrid title="Household members per user" dist={stats.members_dist} />
            </div>

            <div style={{ textAlign: 'center', fontSize: 11, color: C.muted, fontFamily: cal }}>
              Read-only · no new data collection · no third-party analytics
            </div>
          </>
        )}

      </div>
    </div>
  )
}
